/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * VerticalSubItems — slide-up panel with vertical item list.
 *
 * Panel slides up from the bar with a vertical list of sub-items.
 * Includes a star icon on each item for one-tap favoriting on mobile.
 */

import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLayoutStore, useFavorites, useFavoritesDisplayStyle, FAVORITES_MAX } from '@/stores/layoutStore';
import type { BottomNavItem } from './types';

interface Props {
  items: BottomNavItem[];
  onNavigate: () => void;
}

export function VerticalSubItems({ items, onNavigate }: Props) {
  const { t } = useTranslation('common');
  const favorites = useFavorites();
  const displayStyle = useFavoritesDisplayStyle();
  const addFavorite = useLayoutStore((s) => s.addFavorite);
  const removeFavorite = useLayoutStore((s) => s.removeFavorite);
  const isFull = favorites.length >= FAVORITES_MAX[displayStyle];

  return (
    <div className="bn-vertical-panel">
      {items.map((item) => {
        const isFav = favorites.includes(item.href);
        return (
          <div key={item.href} className="bn-vertical-row">
            <NavLink
              to={item.href}
              end
              className={({ isActive }) => cn('bn-vertical-item', isActive && 'bn-vertical-active')}
              onClick={onNavigate}
            >
              <item.icon size={18} />
              <span>{t(item.labelKey)}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className={cn('bn-vertical-badge', item.badgeVariant === 'danger' && 'danger')}>
                  {item.badge}
                </span>
              )}
            </NavLink>
            <button
              className={cn('bn-fav-star', isFav && 'bn-fav-star--active')}
              onClick={(e) => {
                e.stopPropagation();
                if (isFav) removeFavorite(item.href);
                else if (!isFull) addFavorite(item.href);
              }}
              disabled={!isFav && isFull}
              type="button"
              aria-label={isFav ? t('favorites.remove') : t('favorites.add')}
            >
              <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
            </button>
          </div>
        );
      })}

      <style>{`
        .bn-vertical-row {
          display: flex;
          align-items: center;
        }
        .bn-vertical-row .bn-vertical-item {
          flex: 1;
          min-width: 0;
        }
        .bn-fav-star {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: var(--color-text-muted);
          cursor: pointer;
          flex-shrink: 0;
          transition: color 0.15s, transform 0.15s;
        }
        .bn-fav-star:hover:not(:disabled) {
          color: var(--color-warning);
          transform: scale(1.15);
        }
        .bn-fav-star--active {
          color: var(--color-warning);
        }
        .bn-fav-star:disabled {
          opacity: 0.25;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
